// Updates hub: (plugin × site) pair selection with a tri-state parent row, a
// filter bar, a core-update section, fleet cache purge, and a recent-jobs
// table. Aggregation and request building are pure helpers in ./aggregate.

import { useMemo, useState } from 'react';
import { useFleetStatus, useSubmitUpdates, useJob, useAwaitJob, useJobs } from './hooks';
import { JobProgress } from './JobProgress';
import {
  aggregatePendingUpdates,
  aggregateCoreUpdates,
  filterRows,
  rowSelectionState,
  pairKey,
  allPairs,
  buildPairRequest,
  jobProgressLabel,
  type AggregatedUpdate,
  type CoreUpdateRow,
} from './aggregate';
import { QueryState } from '../../components/QueryState';
import type { FleetStatusResponse, JobSummary } from '../../lib/types';

const ID = 'em-mgr--updates';

export interface UpdatesHubProps {
  /** Running job id from the URL (?job=). Null = no job in flight. */
  jobId?: string | null;
  /** Navigate to a job id. Omitted → local fallback state (unit tests). */
  onJobChange?: (jobId: string | null) => void;
}

export function UpdatesHub({ jobId: routeJobId = null, onJobChange }: UpdatesHubProps = {}) {
  const fleetStatus = useFleetStatus();
  const submit = useSubmitUpdates();
  const awaitJob = useAwaitJob();
  const jobs = useJobs();

  // Job id lives in the URL — local state alone lost a running update on refresh/navigation.
  const [localJobId, setLocalJobId] = useState<string | null>(routeJobId);
  const jobId = onJobChange ? routeJobId : localJobId;
  const setJobId = onJobChange ?? setLocalJobId;
  const job = useJob(jobId);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Selection is by (slug, siteId) PAIR so one plugin can be updated on some sites, not others.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedCore, setSelectedCore] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [emOnly, setEmOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  const allRows = useMemo(
    () => aggregatePendingUpdates(fleetStatus.data?.sites ?? []),
    [fleetStatus.data],
  );
  const rows = useMemo(() => filterRows(allRows, { text, emOnly }), [allRows, text, emOnly]);
  const coreRows = useMemo(
    () => aggregateCoreUpdates(fleetStatus.data?.sites ?? []),
    [fleetStatus.data],
  );

  const toggleExpand = (slug: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });

  const setPairs = (keys: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) (on ? next.add(k) : next.delete(k));
      return next;
    });

  const togglePair = (key: string) => setPairs([key], !selected.has(key));

  const toggleRow = (row: AggregatedUpdate) => {
    const keys = row.sites.map((s) => pairKey(row.slug, s.siteId));
    setPairs(keys, rowSelectionState(row, selected) !== 'all');
  };

  const toggleAll = () => {
    const keys = allPairs(rows);
    setPairs(keys, !keys.every((k) => selected.has(k)));
  };

  const toggleCore = (siteId: string) =>
    setSelectedCore((prev) => {
      const next = new Set(prev);
      next.has(siteId) ? next.delete(siteId) : next.add(siteId);
      return next;
    });

  /** Run submissions in sequence: one job per kind, each awaited to terminal. */
  const runSequentially = async (bodies: Parameters<typeof submit.mutateAsync>[0][]) => {
    setBusy(true);
    try {
      for (const body of bodies) {
        const res = await submit.mutateAsync(body);
        setJobId(res.jobId);
        await awaitJob(res.jobId);
      }
    } finally {
      setBusy(false);
    }
  };

  const onSubmitPlugins = async () => {
    const body = buildPairRequest(allRows, selected);
    if (body.updates.length === 0) return;
    await runSequentially([body]);
    setSelected(new Set());
  };

  const onSubmitCore = async () => {
    if (selectedCore.size === 0) return;
    await runSequentially([{
      updates: [...selectedCore].map((siteId) => ({ siteId, slugs: [] })),
      kind: 'updates.core',
    }]);
    setSelectedCore(new Set());
  };

  const onPurgeAll = async () => {
    const siteIds = (fleetStatus.data?.sites ?? [])
      .filter((s) => s.connection !== 'disconnected' && s.connection !== 'never')
      .map((s) => s.siteId);
    if (siteIds.length === 0) return;
    if (!window.confirm(`Purge caches on ${siteIds.length} site(s)?`)) return;
    await runSequentially([{
      updates: siteIds.map((siteId) => ({ siteId, slugs: [] })),
      kind: 'cache.purge',
    }]);
  };

  return (
    <QueryState<FleetStatusResponse>
      idBase={ID}
      isPending={fleetStatus.isPending}
      isError={fleetStatus.isError}
      error={fleetStatus.error}
      data={fleetStatus.data}
    >
      {() => (
        <section data-id={`${ID}--hub`} className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="mr-auto text-lg font-semibold">Updates</h2>
            <button
              type="button"
              data-id={`${ID}--purge-all`}
              disabled={busy}
              onClick={onPurgeAll}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium disabled:opacity-50 dark:border-slate-700"
            >
              Purge all caches
            </button>
            <button
              type="button"
              data-id={`${ID}--submit`}
              disabled={selected.size === 0 || busy}
              onClick={onSubmitPlugins}
              className="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
            >
              {busy ? 'Working…' : `Update selected (${selected.size})`}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <input
              type="search"
              data-id={`${ID}--filter`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Filter by plugin or site…"
              aria-label="Filter updates"
              className="w-64 rounded-md border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
            <label className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                data-id={`${ID}--em-only`}
                checked={emOnly}
                onChange={(e) => setEmOnly(e.target.checked)}
              />
              em-* only
            </label>
          </div>

          {job.data && <JobProgress job={job.data} idBase={ID} />}

          {allRows.length === 0 ? (
            <div
              data-id={`${ID}--uptodate`}
              className="rounded-lg border border-dashed border-green-300 p-6 text-center text-green-700 dark:border-green-800 dark:text-green-400"
            >
              All sites are up to date.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
                  <tr>
                    <th className="w-8 px-3 py-2">
                      <input
                        type="checkbox"
                        data-id={`${ID}--select-all`}
                        aria-label="Select all updates"
                        checked={rows.length > 0 && allPairs(rows).every((k) => selected.has(k))}
                        onChange={toggleAll}
                      />
                    </th>
                    <th className="w-8 px-3 py-2" />
                    <th className="px-3 py-2">Plugin</th>
                    <th className="px-3 py-2">Target</th>
                    <th className="px-3 py-2">Sites</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <RowGroup
                      key={row.slug}
                      row={row}
                      state={rowSelectionState(row, selected)}
                      expanded={expanded.has(row.slug)}
                      selected={selected}
                      onToggleExpand={() => toggleExpand(row.slug)}
                      onToggleRow={() => toggleRow(row)}
                      onTogglePair={togglePair}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {coreRows.length > 0 && (
            <CoreSection
              rows={coreRows}
              selected={selectedCore}
              onToggle={toggleCore}
              onSubmit={onSubmitCore}
              busy={busy}
            />
          )}

          <RecentJobs
            jobs={jobs.data?.jobs ?? []}
            activeJobId={jobId}
            onOpen={(id) => setJobId(id)}
          />
        </section>
      )}
    </QueryState>
  );
}

function RowGroup({
  row,
  state,
  expanded,
  selected,
  onToggleExpand,
  onToggleRow,
  onTogglePair,
}: {
  row: AggregatedUpdate;
  state: 'all' | 'some' | 'none';
  expanded: boolean;
  selected: Set<string>;
  onToggleExpand: () => void;
  onToggleRow: () => void;
  onTogglePair: (key: string) => void;
}) {
  return (
    <>
      <tr data-id={`${ID}--plugin-row`} className="border-t border-slate-100 dark:border-slate-800">
        <td className="px-3 py-2">
          <input
            type="checkbox"
            data-id={`${ID}--select-plugin`}
            checked={state === 'all'}
            // 'some' renders as the indeterminate dash — a plain checkbox can't express partial selection.
            ref={(el) => {
              if (el) el.indeterminate = state === 'some';
            }}
            onChange={onToggleRow}
            aria-label={`Select ${row.name} on all sites`}
          />
        </td>
        <td className="px-3 py-2">
          <button
            type="button"
            data-id={`${ID}--expand`}
            onClick={onToggleExpand}
            aria-expanded={expanded}
            aria-label={`Expand ${row.name}`}
            className="text-slate-500"
          >
            {expanded ? '▾' : '▸'}
          </button>
        </td>
        <td className="px-3 py-2 font-medium">
          {row.name}
          {row.isEm && (
            <span className="ml-1 rounded bg-slate-100 px-1 text-xs dark:bg-slate-800">em</span>
          )}
        </td>
        <td className="px-3 py-2 text-blue-600 dark:text-blue-400">{row.targetVersion}</td>
        <td className="px-3 py-2">{row.siteCount} sites</td>
      </tr>
      {expanded &&
        row.sites.map((s) => {
          const key = pairKey(row.slug, s.siteId);
          return (
            <tr key={s.siteId} data-id={`${ID}--site-row`} className="bg-slate-50/60 dark:bg-slate-900/40">
              <td className="px-3 py-1">
                <input
                  type="checkbox"
                  data-id={`${ID}--select-site`}
                  checked={selected.has(key)}
                  onChange={() => onTogglePair(key)}
                  aria-label={`Select ${row.name} on ${s.siteId}`}
                />
              </td>
              <td />
              <td className="px-3 py-1 pl-8 text-slate-600 dark:text-slate-400">{s.siteId}</td>
              <td className="px-3 py-1 text-slate-500">
                {s.currentVersion} → {s.targetVersion}
              </td>
              <td />
            </tr>
          );
        })}
    </>
  );
}

function CoreSection({
  rows,
  selected,
  onToggle,
  onSubmit,
  busy,
}: {
  rows: CoreUpdateRow[];
  selected: Set<string>;
  onToggle: (siteId: string) => void;
  onSubmit: () => void;
  busy: boolean;
}) {
  return (
    <section data-id={`${ID}--core`} className="space-y-2">
      <div className="flex items-center gap-2">
        <h3 className="mr-auto text-sm font-semibold">Core updates</h3>
        <button
          type="button"
          data-id={`${ID}--core-submit`}
          disabled={selected.size === 0 || busy}
          onClick={onSubmit}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
        >
          Update core ({selected.size})
        </button>
      </div>
      {/* Core updates 403 unless a site opts in; the message surfaces per-site in the job result rather than hiding the row. */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
            <tr>
              <th className="w-8 px-3 py-2" />
              <th className="px-3 py-2">Site</th>
              <th className="px-3 py-2">From</th>
              <th className="px-3 py-2">To</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.siteId} data-id={`${ID}--core-row`} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    data-id={`${ID}--select-core`}
                    checked={selected.has(r.siteId)}
                    onChange={() => onToggle(r.siteId)}
                    aria-label={`Select core update for ${r.siteId}`}
                  />
                </td>
                <td className="px-3 py-2 font-medium">{r.siteId}</td>
                <td className="px-3 py-2">{r.currentVersion}</td>
                <td className="px-3 py-2 text-blue-600 dark:text-blue-400">{r.targetVersion}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RecentJobs({
  jobs,
  activeJobId,
  onOpen,
}: {
  jobs: JobSummary[];
  activeJobId: string | null;
  onOpen: (jobId: string) => void;
}) {
  if (jobs.length === 0) return null;
  return (
    <section data-id={`${ID}--jobs`} className="space-y-2">
      <h3 className="text-sm font-semibold">Recent jobs</h3>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
            <tr>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Sites</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">When</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr
                key={j.jobId}
                data-id={`${ID}--job-row`}
                onClick={() => onOpen(j.jobId)}
                className={`cursor-pointer border-t border-slate-100 dark:border-slate-800 ${
                  j.jobId === activeJobId ? 'bg-slate-100 dark:bg-slate-800' : ''
                }`}
              >
                <td className="px-3 py-2 font-medium">{j.kind}</td>
                <td className="px-3 py-2">{j.siteCount}</td>
                <td className="px-3 py-2">{jobProgressLabel(j.status)}</td>
                <td className="px-3 py-2 text-slate-500">
                  {j.updatedAt ? new Date(j.updatedAt * 1000).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
